/**
 * Form control surfaces, implementing the Control shape token (10px) and the
 * Body/Label type roles from app/design-system/catalog.ts.
 *
 * Kept out of the client module so server-rendered forms can style their own
 * inputs identically.
 */

export type ControlSize = 'sm' | 'md' | 'lg';

/**
 * `min-h-12` on touch viewports keeps controls at the 48px command-target floor and
 * drops to a denser desktop height, matching what buttonStyles.ts already does.
 */
export const controlBaseClasses =
  'w-full rounded-control border bg-surface text-content transition-colors placeholder:text-content-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-1 focus-visible:ring-offset-canvas disabled:cursor-not-allowed disabled:opacity-60 motion-reduce:transition-none';

export const controlSizeClasses: Record<ControlSize, string> = {
  sm: 'min-h-12 px-2.5 py-1.5 text-xs md:min-h-9',
  md: 'min-h-12 px-3 py-2 text-sm md:min-h-10',
  lg: 'min-h-12 px-3.5 py-2.5 text-base md:min-h-11',
};

export const controlToneClasses = {
  default: 'border-line',
  invalid: 'border-critical',
} as const;

/** Label role from the catalog type scale. */
export const fieldLabelClasses = 'text-[11px] font-bold uppercase tracking-[0.16em] text-content-muted';

export const fieldHintClasses = 'text-xs text-content-muted';

export const fieldErrorClasses = 'text-xs font-medium text-critical';

export function controlClassName({
  size = 'md',
  invalid = false,
  className = '',
}: { size?: ControlSize; invalid?: boolean; className?: string } = {}): string {
  const tone = invalid ? controlToneClasses.invalid : controlToneClasses.default;
  return `${controlBaseClasses} ${controlSizeClasses[size]} ${tone} ${className}`.trim();
}
